import { useState } from 'react'
import { X, Send, Calendar, Clock } from 'lucide-react'
import { useWhatsAppMailboxes } from '@hooks/useWhatsAppMailboxes'
import { useWaTemplates } from '@hooks/useWaTemplates'
import { useCreateWaCampaign } from '@hooks/useWaCampaigns'
import { WaTemplate } from '@/types'
import { toast } from 'react-hot-toast'

interface CreateWaCampaignDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface CampaignFormData {
  accountId: number | null
  name: string
  templateId: number | null
  scheduleType: 'immediate' | 'scheduled'
  scheduledAt: string
}

export function CreateWaCampaignDialog({ isOpen, onClose, onSuccess }: CreateWaCampaignDialogProps) {
  const [formData, setFormData] = useState<CampaignFormData>({
    accountId: null,
    name: '',
    templateId: null,
    scheduleType: 'immediate',
    scheduledAt: '',
  })

  const { data: mailboxesData } = useWhatsAppMailboxes()
  const mailboxes = mailboxesData?.data || []
  const { data: templatesData } = useWaTemplates({ isActive: true })
  const templates = templatesData?.data || []
  const createCampaign = useCreateWaCampaign()

  const handleClose = () => {
    setFormData({
      accountId: null,
      name: '',
      templateId: null,
      scheduleType: 'immediate',
      scheduledAt: '',
    })
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.accountId) {
      toast.error('Please select a WhatsApp account')
      return
    }

    if (!formData.name.trim()) {
      toast.error('Campaign name is required')
      return
    }

    if (!formData.templateId) {
      toast.error('Please select a message template')
      return
    }

    if (formData.scheduleType === 'scheduled' && !formData.scheduledAt) {
      toast.error('Please select a scheduled time')
      return
    }

    try {
      await createCampaign.mutateAsync({
        accountId: formData.accountId,
        name: formData.name.trim(),
        templateId: formData.templateId,
        type: formData.scheduleType === 'scheduled' ? 'SCHEDULED' : 'IMMEDIATE',
        scheduledAt:
          formData.scheduleType === 'scheduled'
            ? new Date(formData.scheduledAt).toISOString()
            : undefined,
      })

      toast.success('Campaign created successfully')
      handleClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create campaign')
    }
  }

  const selectedTemplate = templates.find((t: WaTemplate) => t.id === formData.templateId)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Create WhatsApp Campaign</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Campaign Name */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Campaign Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Summer Promotion 2025"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          {/* WhatsApp Account Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              WhatsApp Account <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.accountId || ''}
              onChange={(e) =>
                setFormData({ ...formData, accountId: e.target.value ? Number(e.target.value) : null })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            >
              <option value="">Select an account...</option>
              {mailboxes
                ?.filter((mb) => mb.isConnected)
                .map((mailbox) => (
                  <option key={mailbox.id} value={mailbox.id}>
                    {mailbox.name} ({mailbox.phoneNumber})
                  </option>
                ))}
            </select>
          </div>

          {/* Template Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Message Template <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.templateId || ''}
              onChange={(e) =>
                setFormData({ ...formData, templateId: e.target.value ? Number(e.target.value) : null })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            >
              <option value="">Select a template...</option>
              {templates.map((template: WaTemplate) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.type})
                </option>
              ))}
            </select>
            {selectedTemplate && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 border">
                <p className="font-medium text-gray-700 mb-1">Preview:</p>
                <p className="whitespace-pre-wrap">{selectedTemplate.content?.substring(0, 200)}...</p>
              </div>
            )}
          </div>

          {/* Schedule Type */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">When to Send</label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scheduleType"
                  value="immediate"
                  checked={formData.scheduleType === 'immediate'}
                  onChange={() => setFormData({ ...formData, scheduleType: 'immediate', scheduledAt: '' })}
                  className="text-green-600 focus:ring-green-500"
                />
                <Clock size={16} className="text-gray-500" />
                <span className="text-sm text-gray-700">Start Immediately</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scheduleType"
                  value="scheduled"
                  checked={formData.scheduleType === 'scheduled'}
                  onChange={() => setFormData({ ...formData, scheduleType: 'scheduled' })}
                  className="text-green-600 focus:ring-green-500"
                />
                <Calendar size={16} className="text-gray-500" />
                <span className="text-sm text-gray-700">Schedule for Later</span>
              </label>
            </div>
          </div>

          {/* Scheduled Time */}
          {formData.scheduleType === 'scheduled' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Scheduled Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.scheduledAt}
                onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Next Steps</h4>
            <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
              <li>After creating the campaign, you&apos;ll add recipients</li>
              <li>Recipients can be added by selecting contacts or tags</li>
              <li>Review and start the campaign when ready</li>
            </ul>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            disabled={createCampaign.isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createCampaign.isPending}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            <span>{createCampaign.isPending ? 'Creating...' : 'Create Campaign'}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
